// Game content text (items, modes, tracks, workouts, achievements…) in the active language.
// The core carries the English text (source language); other languages provide a translation by identifier,
// and every lookup falls back to the core text when a translation is missing (content added without a translation,
// custom workout).
import {
  ITEMS,
  EFFECTS,
  MODES,
  DIFFICULTY,
  TRACKS,
  LIBRARY,
  PLANS,
  CATEGORIES,
  ZONES,
  ACHIEVEMENTS,
  CHALLENGES,
  challengeForTrack,
  TITLES,
  LOOKS,
  findBuiltinEvent,
  type EventDef,
  type Lang,
  type ItemId,
  type EffectId,
  type ModeId,
  type DifficultyId,
  type LookSlot,
  type Unlock,
  type Workout,
  type XpLine,
} from '@gruvetto/core';
import { lang, t, fmtNumber, fmtOrdinal } from './index.js';
import { fmtTime } from '@gruvetto/core';
import contentFr, { legacyPhrases } from './content-fr.js';
import type { ErrorPattern } from './content-fr-errors.js';
import { factValuesFr, factValuesEn, rulePatternsFr } from './content-fr-facts.js';
import type { EventText } from './content-fr-events.js';

type Named = { name?: string; desc?: string };
/** Content translations for a language other than the source language. Every entry is optional. */
export interface ContentDict {
  items?: Partial<Record<ItemId, { name?: string; help?: string }>>;
  effects?: Partial<Record<EffectId, string>>;
  modes?: Partial<Record<ModeId, Named>>;
  difficulty?: Partial<Record<DifficultyId, string>>;
  tracks?: Record<string, Named>;
  workouts?: Record<string, Named>;
  plans?: Record<string, Named>;
  categories?: Record<string, string>;
  zones?: Record<number, string>;
  achievements?: Record<string, Named>;
  /** Legendary challenges, by identifier. */
  challenges?: Record<string, Named>;
  /** Level titles, by starting level (1, 3, 6…). */
  titles?: Record<number, string>;
  looks?: Partial<Record<LookSlot, Record<string, string>>>;
  /** Unlocks, by key `${kind}:${slot ?? ''}:${id}`. */
  unlocks?: Record<string, string>;
  /** Workout block instructions, by exact English text ("Warm-up", "At threshold"…). */
  phrases?: Record<string, string>;
  /** Render moods and graphics quality levels (render/view.ts), by identifier. */
  moods?: Record<string, string>;
  quality?: Record<string, string>;
  /** Server and core error messages, by exact English text. */
  errors?: Record<string, string>;
  /** Messages with runtime values (counts, names): patterns tried after the exact keys. `inner` translates a nested message. */
  errorPatterns?: (inner: (msg: string) => string) => ErrorPattern[];
  /** System messages of the room chat ("NAME joined the room"). */
  systemChat?: ErrorPattern[];
  /** Library events of the core, by identifier. */
  events?: Record<string, EventText>;
  /** Calendar events, by English name of their template. */
  calendar?: Record<string, EventText>;
  /** Custom jersey names of library events, by exact English text. */
  eventJerseys?: Record<string, string>;
  /** Official creations without a core table (games, theme descriptions, wheels), by catalogue identifier. */
  official?: Record<string, Named>;
}

// English is the source language and needs no dictionary.
const CONTENT: Partial<Record<Lang, ContentDict>> = { fr: contentFr };
const dict = (): ContentDict => CONTENT[lang()] ?? {};

export const itemName = (id: ItemId): string => dict().items?.[id]?.name ?? ITEMS[id]?.name ?? id;
export const itemHelp = (id: ItemId): string => dict().items?.[id]?.help ?? ITEMS[id]?.help ?? '';
/** Effect label with its emoji ("⚡ Turbo"). */
export const effectLabel = (id: EffectId): string => dict().effects?.[id] ?? EFFECTS[id]?.[0] ?? id;
export const modeName = (id: string): string => dict().modes?.[id as ModeId]?.name ?? MODES[id as ModeId]?.name ?? id;
export const modeDesc = (id: string): string => dict().modes?.[id as ModeId]?.desc ?? MODES[id as ModeId]?.desc ?? '';
export const difficultyName = (id: string): string => dict().difficulty?.[id as DifficultyId] ?? DIFFICULTY[id as DifficultyId]?.name ?? id;
export const trackName = (id: string): string => dict().tracks?.[id]?.name ?? TRACKS[id]?.name ?? challengeForTrack(id)?.track.name ?? id;
export const trackDesc = (id: string): string => dict().tracks?.[id]?.desc ?? TRACKS[id]?.desc ?? challengeForTrack(id)?.track.desc ?? '';
export const challengeName = (id: string): string => dict().challenges?.[id]?.name ?? CHALLENGES.find(c => c.id === id)?.name ?? id;
export const challengeDesc = (id: string): string => dict().challenges?.[id]?.desc ?? CHALLENGES.find(c => c.id === id)?.desc ?? '';

/** Workout name: custom workouts keep the name chosen by the player. */
const builtin = (w: { id: string; custom?: boolean }) => !w.custom && LIBRARY.some(x => x.id === w.id);
export const workoutName = (w: Pick<Workout, 'id' | 'name'> & { custom?: boolean }): string =>
  (builtin(w) ? dict().workouts?.[w.id]?.name : undefined) ?? w.name;
export const workoutDesc = (w: Pick<Workout, 'id' | 'desc'> & { custom?: boolean }): string =>
  (builtin(w) ? dict().workouts?.[w.id]?.desc : undefined) ?? w.desc ?? '';
export const planName = (id: string): string => dict().plans?.[id]?.name ?? PLANS.find(p => p.id === id)?.name ?? id;
export const planDesc = (id: string): string => dict().plans?.[id]?.desc ?? PLANS.find(p => p.id === id)?.desc ?? '';
export const categoryName = (id: string): string => dict().categories?.[id] ?? (CATEGORIES as Record<string, string>)[id] ?? id;
export const zoneName = (id: number): string => dict().zones?.[id] ?? ZONES.find(z => z.id === id)?.name ?? String(id);
export const achievementName = (id: string): string => dict().achievements?.[id]?.name ?? ACHIEVEMENTS.find(a => a.id === id)?.name ?? id;
export const achievementDesc = (id: string): string => dict().achievements?.[id]?.desc ?? ACHIEVEMENTS.find(a => a.id === id)?.desc ?? '';

/** Title for a level ("Rouleur" → "Rouleur" in French, "Beginner" → "Débutant"…). */
export function titleName(level: number): string {
  const [from, src] = TITLES.filter(([l]) => level >= l).pop() ?? TITLES[0];
  return dict().titles?.[from] ?? src;
}
/** Title of an entry in the titles table. */
export const titleAt = (from: number): string => dict().titles?.[from] ?? TITLES.find(([l]) => l === from)?.[1] ?? '';
export const lookName = (slot: LookSlot, id: string): string => dict().looks?.[slot]?.[id] ?? LOOKS[slot]?.[id] ?? id;
export const unlockName = (u: Pick<Unlock, 'kind' | 'slot' | 'id' | 'name'>): string => dict().unlocks?.[`${u.kind}:${u.slot ?? ''}:${u.id}`] ?? u.name;
export const unlockKey = (u: Pick<Unlock, 'kind' | 'slot' | 'id'>): string => `${u.kind}:${u.slot ?? ''}:${u.id}`;
export const moodName = (id: string, fallback: string): string => dict().moods?.[id] ?? fallback;
export const qualityName = (id: string, fallback: string): string => dict().quality?.[id] ?? fallback;

/**
 * Instruction of a workout block. Handles the repetitions generated by the core ("At threshold 2/3")
 * and the free-form instructions of a custom workout, left as they are. Text saved when French was the source
 * language (copied built-in workouts) is shown in English through `legacyPhrases`.
 */
export function phrase(text: string): string {
  if (!text) return text;
  const p = lang() === 'en' ? legacyPhrases : dict().phrases;
  if (!p) return text;
  if (p[text]) return p[text];
  const m = /^(.*) (\d+\/\d+)$/.exec(text);
  if (m && p[m[1]]) return `${p[m[1]]} ${m[2]}`;
  return text;
}

let patternCache: { d: ContentDict; p: ErrorPattern[] } | null = null;
const patternsOf = (d: ContentDict, inner: (m: string) => string): ErrorPattern[] => {
  if (patternCache?.d !== d) patternCache = { d, p: d.errorPatterns?.(inner) ?? [] };
  return patternCache.p;
};

/** Error message from the server or the core (English text) in the active language. */
export function errorText(msg: string): string {
  const e = dict().errors;
  if (!e || !msg) return msg;
  const one = (m: string): string => {
    if (e[m]) return e[m];
    for (const [re, fmt] of patternsOf(dict(), one)) {
      const x = re.exec(m);
      if (x) return fmt(x);
    }
    // API validation error: "field: message"; the field name is left as is.
    const field = /^([\w.[\]]*): (.*)$/.exec(m);
    if (field) return `${field[1] ? field[1] + ': ' : ''}${one(field[2])}`;
    // Parameterised messages: "Inconsistent samples (p)"
    const arg = /^(.*) \(([^)]*)\)$/.exec(m);
    if (arg && e[arg[1]]) return `${e[arg[1]]} (${arg[2]})`;
    // Free prefix (name of a workout or an item): "Name: workout too short (4 min, 5 min at least)". The prefix stays as it is.
    for (let i = m.indexOf(': '); i > 0; i = m.indexOf(': ', i + 2)) {
      const rest = one(m.slice(i + 2));
      if (rest !== m.slice(i + 2)) return `${m.slice(0, i)} : ${rest}`;
    }
    return m;
  };
  return msg.split(' · ').map(one).join(' · ');
}

/** Text of a chat message: the system messages of the game server ("NAME joined the room") are translated, the players' text never is. */
export function chatText(m: { from?: string; text: string }): string {
  if (m.from !== '📣') return m.text;
  for (const [re, fmt] of dict().systemChat ?? []) {
    const x = re.exec(m.text);
    if (x) return fmt(x);
  }
  return m.text;
}

// ---------- Events of the core library and calendar ----------
const builtinCache = new Map<string, EventDef | null>();
/** The built-in event this definition comes from, only while it is untouched (a copy edited by a player keeps its text). */
const builtinOf = (def: Pick<EventDef, 'id'>): EventDef | null => {
  let b = builtinCache.get(def.id);
  if (b === undefined) { b = findBuiltinEvent(def.id); builtinCache.set(def.id, b); }
  return b;
};
const eventText = (def: Pick<EventDef, 'id' | 'name'>, src: EventDef): EventText | undefined =>
  def.id.startsWith('cal-') ? dict().calendar?.[src.name] : dict().events?.[def.id];

export function eventName(def: Pick<EventDef, 'id' | 'name'>): string {
  const b = builtinOf(def);
  return (b && b.name === def.name ? eventText(def, b)?.name : undefined) ?? def.name;
}
export function eventDesc(def: Pick<EventDef, 'id' | 'name' | 'desc'>): string {
  const b = builtinOf(def);
  return (b && b.desc === def.desc ? eventText(def, b)?.desc : undefined) ?? def.desc;
}
/** Name of stage `i`: library and calendar stages are translated; the automatic name "Stage 2 · Hills Circuit" is rebuilt. */
export function stageName(def: Pick<EventDef, 'id' | 'name' | 'stages'>, i: number): string {
  const st = def.stages[i], b = builtinOf(def);
  if (!st) return '';
  const tr = b && b.stages[i]?.name === st.name ? eventText(def, b)?.stages[i]?.name : undefined;
  if (tr) return tr;
  const auto = /^Stage (\d+) · (.+)$/.exec(st.name);
  if (auto && TRACKS[st.track]?.name === auto[2] && lang() !== 'en') return `${t.events.screen.stageN(+auto[1])} · ${trackName(st.track)}`;
  return st.name;
}
export function stageDesc(def: Pick<EventDef, 'id' | 'stages'>, i: number): string {
  const st = def.stages[i], b = builtinOf(def);
  if (!st) return '';
  return (b && b.stages[i]?.desc === st.desc ? eventText(def as Pick<EventDef, 'id' | 'name'>, b)?.stages[i]?.desc : undefined) ?? st.desc ?? '';
}
/** Name of a custom jersey of a library event ("Leader’s jersey"). */
export const eventJerseyName = (name: string): string => dict().eventJerseys?.[name] ?? name;

/** Name and description of an official creation without a core table (games, themes, wheels). */
export const officialName = (id: string, fallback: string): string => dict().official?.[id]?.name ?? fallback;
export const officialDesc = (id: string, fallback: string): string => dict().official?.[id]?.desc || fallback;

/**
 * Displayed name of an activity, rebuilt in the active language from its metadata when possible. Names stored before
 * English became the source language are French and new ones are English, so the stored name is only a fallback.
 */
export function activityName(a: { type?: string; name: string; mode?: string | null; track?: string | null; workoutId?: string | null; challenge?: string | null }): string {
  if (a.type === 'race' && a.challenge && CHALLENGES.some(c => c.id === a.challenge)) return challengeName(a.challenge);
  if (a.type === 'race' && a.mode && a.track && MODES[a.mode as ModeId] && TRACKS[a.track]) return `${modeName(a.mode)} · ${trackName(a.track)}`;
  if (a.type === 'workout' && a.workoutId) {
    const w = LIBRARY.find(x => x.id === a.workoutId);
    if (w) return workoutName(w);
  }
  return a.name;
}

/** XP line in the active language (the core provides `key` and `n`; old lines: stored label). */
export function xpLineLabel(l: XpLine): string {
  const x = t.common.xp;
  switch (l.key) {
    case 'distance':
      return x.distance(fmtNumber(l.n ?? 0, 1));
    case 'effort':
      return x.effort(l.n ?? 0);
    case 'win':
      return x.win;
    case 'podium':
      return x.podium(fmtOrdinal(l.n ?? 2));
    case 'finished':
      return x.finished;
    case 'online':
      return x.online;
    case 'hits':
      return x.hits(l.n ?? 0);
    case 'workout':
      return x.workout;
    case 'game':
      return x.game;
    case 'test':
      return x.test;
    case 'record':
      return x.record(fmtTime(l.n ?? 0));
    case 'firstOfDay':
      return x.firstOfDay;
    case 'challenge':
      return l.ref ? x.challenge(challengeName(l.ref)) : l.label;
    default:
      return l.label;
  }
}

/** A creation's key figure value (a list joined with ", " is translated item by item). Unknown values stay as they are. */
export function factValue(value: string): string {
  const map = lang() === 'fr' ? factValuesFr : factValuesEn;
  return value.split(', ').map(v => map[v] ?? v).join(', ');
}

/** A mode rule line from `describeRules`, in the active language (the core writes it in English). */
export function ruleText(text: string): string {
  if (lang() !== 'fr') return text;
  for (const [re, fmt] of rulePatternsFr) { const m = re.exec(text); if (m) return fmt(m); }
  return text;
}
